import * as React from 'react'
import DeleteIcon from '../../../assets/icons_new/DeleteIcon.svg'
import { createPortal } from 'react-dom'
import { FieldErrorMessage, FieldHelper, Input } from '../primitives'
import { useStableId } from '../hooks/useStableId'
import DatePickerPopup from '@/ui/date/DatePickerPopup'
import { faCalendar, FaIcon } from '@/lib/fa'


const formatToMonthDisplay = (value?: string | null): string => {
  if (!value) return ''
  const match = value.match(/^(\d{4})-(\d{2})/)
  if (match) return `${match[2]}-${match[1]}`
  return value
}

const parseMonthInput = (input: string): string | null => {
  const trimmed = input.trim()
  const match = trimmed.match(/^(\d{2})-(\d{4})$/)
  if (!match) return null

  const month = parseInt(match[1], 10)
  const year = parseInt(match[2], 10)

  if (year < 1900 || year > 2100) return null
  if (month < 1 || month > 12) return null

  return `${year}-${String(month).padStart(2, '0')}-01`
}

const formatMonthWithDash = (digits: string): string => {
  let result = ''
  for (let i = 0; i < digits.length; i++) {
    if (i === 2) {
      result += '-'
    }
    result += digits[i]
  }
  return result
}

type MonthFieldProps = {
  openDirection?: 'down' | 'up'
  setValue: (fullDate: string | null) => void
  label?: string
  value?: string | null
  placeholder?: string
  helper?: React.ReactNode
  error?: string | null
  disabled?: boolean
  id?: string
  required?: boolean
}

export const MonthField: React.FC<MonthFieldProps> = ({
  setValue,
  openDirection = 'down',
  label,
  value,
  error,
  disabled,
  id,
  required,
  helper,
  placeholder = 'MM-RRRR',
}) => {
  const DROPDOWN_HEIGHT = 400

  const autoId = useStableId()
  const inputId = id ?? `autoid-${autoId}`
  const helperId = helper ? `${inputId}-helper` : undefined
  const errorId = error ? `${inputId}-error` : undefined

  const describedBy =
    [helperId, errorId].filter(Boolean).join(' ') || undefined
  const [open, setOpen] = React.useState(false)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
  })

  const [inputValue, setInputValue] = React.useState('')

  const cursorPosRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    if (cursorPosRef.current !== null && inputRef.current) {
      const pos = cursorPosRef.current
      inputRef.current.setSelectionRange(pos, pos)
      cursorPosRef.current = null
    }
  })

  React.useEffect(() => {
    setInputValue(formatToMonthDisplay(value))
  }, [value])

  const handleCalendarIconClick = (e: React.MouseEvent) => {
    if (disabled) return
    e.stopPropagation()

    const willOpen = !open

    if (willOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()

      let top

      if (openDirection === 'down') {
        top = rect.bottom + window.scrollY + 4
        const spaceBelow = window.innerHeight - rect.bottom
        if (spaceBelow < DROPDOWN_HEIGHT) {
          top = rect.top + window.scrollY - DROPDOWN_HEIGHT - 4
        }
      } else {
        top = rect.top + window.scrollY - DROPDOWN_HEIGHT - 4
        if (rect.top < DROPDOWN_HEIGHT) {
          top = rect.bottom + window.scrollY + 4
        }
      }

      setDropdownPosition({
        top,
        left: rect.left + window.scrollX,
      })
    }

    setOpen(willOpen)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    const cursorPos = e.target.selectionStart ?? newValue.length

    const newDigits = newValue.replace(/\D/g, '')
    // Limit to 6 digits (MMYYYY)
    const limitedDigits = newDigits.slice(0, 6)
    const formatted = formatMonthWithDash(limitedDigits)

    let newCursorPos = 0
    let digitCount = 0
    for (let i = 0; i < formatted.length; i++) {
      if (digitCount === cursorPos) break
      newCursorPos = i + 1
      if (formatted[i] !== '-') {
        digitCount++
      }
    }
    if (
      newCursorPos < formatted.length &&
      formatted[newCursorPos] === '-' &&
      newValue.length >= inputValue.length
    ) {
      newCursorPos++
    }

    cursorPosRef.current = newCursorPos
    setInputValue(formatted)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      inputRef.current?.blur()
    }
  }

  const handleInputBlur = () => {
    const parsed = parseMonthInput(inputValue)

    if (parsed) {
      setValue(parsed)
    } else if (inputValue === '') {
      setValue(null)
    } else {
      setInputValue(formatToMonthDisplay(value))
    }
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setValue(null)
    setOpen(false)
  }

  const handleConfirm = (fullDate: string) => {
    // Extract only month and year, set day to 01
    const match = fullDate.match(/^(\d{4})-(\d{2})/)
    if (match) {
      setValue(`${match[1]}-${match[2]}-01`)
    } else {
      setValue(fullDate)
    }
    setOpen(false)
  }

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div
      ref={containerRef}
      className="tw-relative tw-z-[50] tw-inline-block tw-w-full"
    >
      {label && (
        <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">
          {required && <span className="tw-mr-0.5 tw-text-error">*</span>}
          {label}
        </label>
      )}

      <div className="tw-relative tw-flex tw-items-center">
        <Input
          ref={inputRef}
          disabled={disabled}
          aria-describedby={describedBy}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          required={required}
          placeholder={placeholder}
          className="tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-leading-tight tw-placeholder-gray-400 tw-shadow-sm focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-cursor-not-allowed disabled:tw-bg-gray-100 disabled:tw-text-gray-400 data-[invalid=true]:tw-border-red-500 data-[invalid=true]:focus:tw-ring-red-500"
        />
        <div className="tw-absolute tw-right-3 tw-flex tw-items-center tw-gap-2">
          {value && !disabled && (
            <button
              type="button"
              onClick={handleDeleteClick}
              className="tw-flex tw-items-center tw-justify-center tw-border-none tw-bg-transparent tw-p-0 hover:tw-opacity-70"
            >
              <img
                src={DeleteIcon}
                alt="Delete Icon"
                className="tw-h-4 tw-w-4"
              />
            </button>
          )}
          <button
            type="button"
            onClick={handleCalendarIconClick}
            disabled={disabled}
            className={`tw-flex tw-items-center tw-justify-center tw-border-none tw-bg-transparent tw-p-0 ${disabled ? 'tw-cursor-not-allowed tw-opacity-50' : 'tw-cursor-pointer'}`}
          >
            <FaIcon icon={faCalendar} className="tw-h-4 tw-w-4" />
          </button>
        </div>
      </div>

      {helper && (
        <FieldHelper
          id={helperId}
          className="tw-mt-0.5 tw-text-xs tw-text-gray-500"
        >
          {helper}
        </FieldHelper>
      )}

      {error && (
        <FieldErrorMessage
          id={errorId}
          className="tw-mt-0.5 tw-text-xs tw-font-medium tw-text-red-600"
        >
          {error}
        </FieldErrorMessage>
      )}

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="tw-absolute tw-z-[9999] tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            <DatePickerPopup value={value ?? null} submit={handleConfirm} />
          </div>,
          document.body,
        )}
    </div>
  )
}
